import React, { useState, useEffect, useRef, memo, useMemo, useLayoutEffect } from 'react';
import { Client } from '@twilio/conversations';
import './WebChat.css'; // Asegúrate de crear este archivo CSS
import { ActivityIndicator, Image, Linking, ScrollView, Text, TouchableOpacity, View, StyleSheet, useWindowDimensions, Pressable, } from 'react-native';
import { getStatusAgent, getChangeStatusAgent, createSendMessage, getMediaChat, createSendMedia, updateChannelAttributes, sendTemplate, postAssign, postNotes, getNotes } from "../../controller/chatController.ts";
import { useAppDispatch, useAppSelector } from "../../store/hooks.ts";
import { Button, IconButton, Icon, Surface } from 'react-native-paper';
import ReactAudioPlayer from 'react-audio-player';
import Colors from '../../resources/Colors.ts';
import GlobalStyles from "../../resources/Styles.ts";
import { CecyIA, Roles, Status } from "../../utils/constants";
import { ModalContent } from '../../components/modal/ModalTemplate.tsx';
import ChatActionArea from '../../components/Chat/ChatActionArea.tsx';
import SelectField from '../../components/Form/SelectField';

const ChatScreenComponent = (props: any) => {
  const { channel, onDialog, onClose, onUpdateAttributes } = props;
  const { height } = useWindowDimensions();

  const dispatch = useAppDispatch();
  const chat = useAppSelector(state => state.chat);
  const user = useAppSelector(state => state.user);
  const auth = useAppSelector(state => state.auth);

  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [paginator, setPaginator] = useState([]);
  const [resetIA, setResetIA] = useState(false);
  const [scrollEnd, setScrollEnd] = useState(true);
  const prevScrollHeightRef = useRef(0);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [msgClose, setMsgClose] = useState(false);

  const [assignTo, setAssignTo] = useState("");
  const [hasNewMessagesBelow, setHasNewMessagesBelow] = useState(false);
  const [firstUnreadId, setFirstUnreadId] = useState(null);

  const messagesEndRef = useRef(null);
  const messagesTopRef = useRef(null);
  const fileInputRef = useRef(null);

  // 1. Declaras la referencia aquí
  const currentSidRef = useRef(channel?.sid);

  useEffect(() => {
    getMessages();
  }, []);

  useEffect(() => {
    //hay que cambiar el estatus si este no es del id que le corresponde
    setResetIA(chat.statusAgent)
  }, [chat.statusAgent])

  // Este efecto asegura que si el broker cambia externamente, el select se actualice
  useEffect(() => {
    const bId = channel.brokerId || channel.attributes?.broker_id;
    setAssignTo(bId);
  }, [channel.brokerId, channel.attributes?.broker_id]);

  // 2. Cleanup en el useEffect
  useEffect(() => {
    currentSidRef.current = channel?.sid;
    const client = channel.twilioClient;
    if (client) {
      getStatusChannelAgent();
      getNotesChat();
      setupConversation(client);
    }

    const container = messagesTopRef.current;
    if (container) {
      // Al cambiar de chat, mandamos el scroll al final de inmediato
      container.scrollTop = container.scrollHeight;
    }

    // Reseteamos nuestras banderas de control
    //setScrollEnd(true);
    prevScrollHeightRef.current = 0;

    setHasMoreMessages(false);

    return () => {
      if (client) {
        // 🚩 IMPORTANTE: Solo removemos NUESTRA función, no la del padre
        client.off('messageAdded', onMessageAdded);
      }
    };
  }, [channel?.sid]);

  useLayoutEffect(() => {
    const container = messagesTopRef.current;
    if (!container) return;

    // CASO 1: Carga de mensajes antiguos (Anclaje)
    if (prevScrollHeightRef.current > 0) {
      const addedHeight = container.scrollHeight - prevScrollHeightRef.current;
      container.scrollTop = addedHeight;
      prevScrollHeightRef.current = 0;
    }
    // CASO 2: Forzar ir al fondo (Cambio de chat o mensaje nuevo estando abajo)
    else if (scrollEnd) {
      container.scrollTop = container.scrollHeight;
      setScrollEnd(false);
    }
  }, [messages, scrollEnd]);

  const templatesAgrupados = useMemo(() => {
    if (!chat.templates) return {};

    return chat.templates.reduce((acc, template) => {
      // Según tu imagen, el ID de grupo está en body[1]
      const grupoId = template.body[1];
      const body = template.body[0];

      // Si el grupo no existe en nuestro acumulador, lo creamos
      if (!acc[grupoId]) {
        acc[grupoId] = [];
      }

      // Guardamos solo el SID y el texto
      acc[grupoId].push({
        sid: template.sid,
        body: body
      });

      return acc;
    }, {});
  }, [chat.templates]);

  useLayoutEffect(() => {
    const container = messagesTopRef.current;

    // Si tenemos una altura previa guardada, significa que acabamos de cargar mensajes arriba
    if (prevScrollHeightRef.current > 0 && container) {
      // Calculamos la nueva altura total
      const newScrollHeight = container.scrollHeight;

      // La diferencia es exactamente el tamaño de los mensajes nuevos
      const addedHeight = newScrollHeight - prevScrollHeightRef.current;

      // Ajustamos el scroll para compensar el crecimiento
      container.scrollTop = addedHeight;

      // Limpiamos la referencia
      prevScrollHeightRef.current = 0;
    }
    
    if (messages.length > 0) {
      calcularEstadoVentana(messages);
    }
  }, [messages]); // Se dispara cada vez que el array de mensajes cambia

  //Calculamos el ultimo mensaje de la ventana del chat
  const calcularEstadoVentana = (listaMensajes) => {
    // 1. Buscamos el último mensaje donde el 'from' sea el cliente
    const ultimoMensajeCliente = [...listaMensajes]
      .reverse()
      .find(m => m.user.name !== "bot");

    if (!ultimoMensajeCliente) {
      setMsgClose(true);
    }

    // 2. Obtenemos la fecha de ese mensaje
    const lastDate = new Date(ultimoMensajeCliente.createdAt);
    const nowDate = new Date();

    // 3. Calculamos la diferencia en horas
    const milisegundos = nowDate - lastDate;
    const horasTranscurridas = milisegundos / (1000 * 60 * 60);

    setMsgClose(horasTranscurridas >= 24);
  };

  const getMessages = () => {
    setLoading(false);
  }

  const handleReset = () => {
    dispatch(getChangeStatusAgent({
      enabled: !resetIA,
      conversation_sid: channel.sid,
      org_id: channel.orgId,
      whatsapp_number: channel.whatsapp,
    }))
  }

  const getStatusChannelAgent = () => {
    dispatch(getStatusAgent(channel.sid, channel.orgId))
  }

  const changeState = async () => {
    await updateChannelAttributes(channel.sid, {
      "cecy_status": "manual_pause",
      "human_supervisor": true,
      "priority": "normal",
      org_id: channel.orgId,
      whatsapp_number: channel.whatsapp,
    });
  }

  const sorterConver = (messages) => {
    // 🔄 Ordenamiento por fecha createdAt
    const sortedMessages = [...messages].sort((a, b) => {
      // Convertimos a objeto Date para comparar correctamente
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();

      return dateA - dateB; // Orden ascendente: los más viejos primero
    });
    return sortedMessages
  }

  const onMessageAdded = async (message) => {
    if (message.conversation.sid === currentSidRef.current) {
      const newMessage = await convertTwilioMessage(message);
      const container = messagesTopRef.current;

      let shouldScroll = false;

      if (container) {
        // Calculamos qué tan lejos está el usuario del fondo
        // scrollHeight (total) - scrollTop (donde estoy) - clientHeight (lo que veo)
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;

        // Si está a menos de 150px del fondo, consideramos que "está viendo el final"
        shouldScroll = distanceFromBottom < 150;
      }

      setMessages(prev => {
        if (prev.some(m => m._id === newMessage._id)) return prev;
        return [...prev, newMessage];
      });

      // Solo activamos el scroll automático si el usuario ya estaba abajo
      if (shouldScroll) {
        setScrollEnd(true);
        setHasNewMessagesBelow(false);
        setFirstUnreadId(null);
      } else {
        setFirstUnreadId(prev => prev === null ? newMessage._id : prev);
        setHasNewMessagesBelow(true); // Un nuevo estado useState(false)
      }
    }
  };

  // Añade este listener al div del scroll en tu JSX
  const handleScroll = (e) => {
    const container = e.target;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;

    // Si el usuario llega al fondo manualmente, quitamos el aviso
    if (distanceFromBottom <= 5) {
      setHasNewMessagesBelow(false);
      setFirstUnreadId(null);
    }
  };

  const setupConversation = async (client: Client) => {
    try {
      // En lugar de borrar todos, solo nos aseguramos de no duplicar esta función
      client.off('messageAdded', onMessageAdded); // Remueve si ya existía

      const conv = await client.peekConversationBySid(channel.sid);

      setConversation(conv);

      const messagesPaginator = await conv.getMessages(20);

      setPaginator(messagesPaginator.items);
      const existingMessagesPromises = messagesPaginator.items.map(convertTwilioMessage);
      const existingMessages = await Promise.all(existingMessagesPromises);

      // 🔄 Ordenamiento por fecha createdAt
      const sortedMessages = sorterConver(existingMessages)

      setMessages([...sortedMessages]);
      setScrollEnd(true);
      if (messagesPaginator.hasPrevPage) {
        setHasMoreMessages(messagesPaginator.hasPrevPage);
      }

      client.on('messageAdded', onMessageAdded); // Agrega la nueva
      client.on('messageUpdated', async ({ message, updateReasons }) => {
        if (updateReasons.includes('deliveryReceipt')) {
          const updatedMsg = await convertTwilioMessage(message);

          setMessages(prev => prev.map(m =>
            m._id === updatedMsg._id ? { ...m, statusMessage: updatedMsg.statusMessage } : m
          ));
        }
      });
    } catch (error) {
      console.error(error);
    }
  };

  const loadMoreMessages = async () => {
    const container = messagesTopRef.current; // El div que tiene el scroll
    if (isFetching || !hasMoreMessages || !container) return;

    setIsFetching(true);

    // GUARDAMOS LA ALTURA ACTUAL (Antes de que aparezcan los nuevos mensajes)
    prevScrollHeightRef.current = container.scrollHeight;

    const messagePaginator = await conversation.getMessages(21, paginator[0].index);
    let newMessages = messagePaginator.items;
    
    if (newMessages.length > 0) {
      newMessages = newMessages.slice(0, -1);
      const existingMessagesPromises = newMessages.map(convertTwilioMessage);
      const existingMessages = await Promise.all(existingMessagesPromises);
      const sortedMessages = sorterConver(existingMessages);
      
      // Actualizamos mensajes y paginador
      setMessages(prevMessages => [...sortedMessages, ...prevMessages]);
      setPaginator(prevPaginator => [...newMessages, ...prevPaginator]);
      
      // IMPORTANTE: No disparamos el scroll automático al final
      setScrollEnd(false);
    }
    
    setHasMoreMessages(messagePaginator.hasPrevPage);
    setIsFetching(false);
  };

  const convertTwilioMessage = async (twilioMessage) => {
    let mediaUrl = null;
    let mediaType = null;
    let mediaName = null;
    const mediaSid = twilioMessage?.media ? twilioMessage?.media.sid : null;

    if (twilioMessage?.media) {
      let mediaJson = await getMediaChat({
        media_sid: twilioMessage.media.sid,
        org_id: channel.orgId,
        whatsapp_number: channel.whatsapp
      });

      if (mediaJson) {
        mediaUrl = mediaJson.links.content_direct_temporary;
        mediaType = mediaJson.content_type;
        mediaName = mediaJson.filename;
      }
    }

    return {
      statusMessage: twilioMessage?.aggregatedDeliveryReceipt?.state || 'sent',
      _id: twilioMessage.sid,
      text: twilioMessage.body,
      createdAt: twilioMessage?.dateCreated,
      mediaSid: mediaSid,
      media: mediaUrl,
      mediaType: mediaType,
      mediaName: mediaName,
      mensaje: twilioMessage,
      user: {
        _id: twilioMessage?.author,
        name: twilioMessage?.author,
      },
    };
  };

  const handleSend = async () => {
    if (fileToUpload && fileToUpload.length > 0) {
      var formData = new FormData();

      // 2. Adjuntar el archivo directamente.
      // El nombre 'file' (o 'media') debe coincidir con lo que tu backend espera
      formData.append('file', fileToUpload[0]);
      formData.append('conversation_sid', channel.sid);
      formData.append('org_id', channel.orgId)
      formData.append('whatsapp_number', channel.whatsapp)

      await dispatch(createSendMedia(formData));
      setFileToUpload(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
    if (newMessage.trim()) {
      await dispatch(createSendMessage({
        conversation_sid: channel.sid,
        content: newMessage,
        org_id: channel.orgId,
        whatsapp_number: channel.whatsapp,
      }))
      setNewMessage('')
    }
  }

  const handleRename = (event: any) => {
    onDialog(conversation?._internalState.friendlyName, true)
  }

  const handleTemplate = (template: any) => {
    onDialog(null, false, (<ModalContent
      template={template}
      onSend={(variable, msg) => {
        const json_ = {
          template_sid: template.sid,
          content_variables: variable,
          org_id: channel.orgId,
          whatsapp_number: channel.whatsapp,
        };
        dispatch(sendTemplate(channel.sid, json_, onClose));
      }}
    />))
  }

  const handleFileChange = (event: any) => {
    const selectedFile = event.target.files;
    if (selectedFile) {
      setFileToUpload(selectedFile);
    }
  };

  const openFilePicker = () => {
    fileInputRef.current.click(); // Programmatically trigger the file input click
  };

  const handleOpenPdf = async (mediaSid) => {
    try {
      // 1. Pedimos un link nuevo justo ahora
      const mediaJson = await getMediaChat({
        media_sid: mediaSid,
        org_id: channel.orgId,
        whatsapp_number: channel.whatsapp
      });

      if (mediaJson && mediaJson.links.content_direct_temporary) {
        const freshUrl = mediaJson.links.content_direct_temporary;

        // 2. Abrimos el link recién generado
        const supported = await Linking.canOpenURL(freshUrl);
        if (supported) {
          await Linking.openURL(freshUrl);
        }
      } else {
        console.error("No se pudo regenerar el enlace del PDF");
      }
    } catch (error) {
      console.error("Error al obtener el PDF fresco:", error);
    }
  };

  const assignToConversation = async (id: string) => {
    let newAssign = user.users.find(x => x.id === id && x.is_active);

    if (!newAssign) {
      //mandar mensaje de alerta
      return;
    }

    if (id === CecyIA) {
      id = "0";
    }

    setAssignTo(id);

    const success = await postAssign(channel.sid, {
      org_id: channel.orgId,
      whatsapp_number: channel.whatsapp,
      broker_id: id,
      group_id: auth.userInfo.group_id
    });

    if (success) {
      // Notificamos al padre usando ambos formatos de nombre para evitar errores de mapeo
      onUpdateAttributes(channel.sid, { broker_id: id, brokerId: id });
    }
  }

  const handleNotes = async (notes: string) => {
    await postNotes(channel.sid, {
      org_id: channel.orgId,
      whatsapp_number: channel.whatsapp,
      notes: notes
    });
  }

  const getNotesChat = () => {
    dispatch(getNotes(channel.sid, channel.orgId, channel.whatsapp));
  }

  const userList = () => {
    return user.users.filter((u: any) => u.is_active === true).map((user: any) => {
      return { value: user.id, label: user.name };
    });
  }

  const WhatsappText = ({ text, user }) => {
    if (!text) return null;
    // Función para procesar el texto con Regex
    const formatText = (content) => {
      // Dividimos el texto buscando los patrones: *bold*, _italic_, ~strike~
      // El orden importa para no romper el anidamiento simple
      const parts = content.split(/(\*[^*]+\*|_[^_]+_|~[^~]+~)/g);

      return parts.map((part, index) => {
        if (part.startsWith('*') && part.endsWith('*')) {
          return <Text key={index} style={{ fontWeight: 'bold' }}>{part.slice(1, -1)}</Text>;
        }
        if (part.startsWith('_') && part.endsWith('_')) {
          return <Text key={index} style={{ fontStyle: 'italic' }}>{part.slice(1, -1)}</Text>;
        }
        if (part.startsWith('~') && part.endsWith('~')) {
          return <Text key={index} style={{ textDecorationLine: 'line-through' }}>{part.slice(1, -1)}</Text>;
        }
        return part; // Texto normal
      });
    };

    return <Text
      style={{ color: text.startsWith("*Transcripción:*") ? "#746c6c" : user === "bot" ? Colors.bgWhite : Colors.blackColor }}
    >{formatText(text)}</Text>;
  };

  if (loading) {
    return (
      <div className="web-chat-container">
        <div className="loading-spinner">
          <ActivityIndicator size="large" />
        </div>
      </div>
    );
  }

  return (
    <View style={[styles.container, { height: height - 55 }]}>
      <View>
        <View style={[styles.horizontalContentTop, { marginVertical: 10, alignItems: "center", flexDirection: "row" }]}>
          <View style={{
            width: 30,
            height: 30,
            borderRadius: 15,
            borderStyle: 'solid',
            backgroundColor: Colors.greenButtonColor,
            justifyContent: 'center'
          }}>
            <Text style={{ fontSize: 16, textAlign: 'center' }}>{channel?.letter}</Text>
          </View>
          <View style={{ flex: 1, marginLeft: 10 }}>
            <Text style={{ fontSize: 16, marginVertical: 5, marginStart: 10 }}>
              {channel?.user} {channel?.user !== channel?.number ? "(" + channel?.number + ")" : null}
              <Pressable
                onPress={() => console.log('Click!')}
                style={({ hovered }) => [{ paddingLeft: 5 }]}
              >
                {({ hovered }) => (
                  <Text style={{ fontSize: 12, color: hovered ? Colors.primary : Colors.blackColor }}
                    onPress={handleRename}>
                    Editar
                  </Text>
                )}
              </Pressable>

            </Text>

            <Text>{Status[conversation?.attributes?.cecy_status?.toUpperCase()]}</Text>
          </View>

          {conversation?.attributes?.cecy_status == "error_handoff" ? (<Button onPress={changeState}>Marcar como atendido</Button>) : null}
          <SelectField
            name={'usuario'}
            placeholder='Sin asignar'
            formValues={{ usuario: assignTo }}
            items={userList()}
            style={{ maxWidth: 150 }}
            readonly={auth.userInfo.role !== Roles.ADMIN}
            onUpdateFormValue={(name, value) => {
              assignToConversation(value)
            }} />
        </View>
      </View>
      <div className="web-chat-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* <ScrollView contentContainerStyle={{ flexGrow: 1 }} style={{ flex: 1 }}> */}
        {/* --- ÁREA DE MENSAJES (Cabecera + Chat) --- */}
        <View style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <div className="messages-list"
            onScroll={handleScroll}
            style={{
              height: "100%",
              display: "flex",
              overflowY: "auto", // Importante que este div sea el que scrollea
              flexDirection: "column"
            }} ref={messagesTopRef}>
            {hasMoreMessages && <Button onPress={loadMoreMessages}>Más mensajes</Button>}
            {messages.map((msg, index) => (
              <React.Fragment key={msg._id}>
                {/* SI EL ID COINCIDE, PINTAMOS LA LÍNEA */}
                {firstUnreadId === msg._id && (
                  <View style={styles.unreadSeparator}>
                    <View style={styles.lineSeparator} />
                    <Text style={styles.unreadText}>Mensajes no leídos</Text>
                    <View style={styles.lineSeparator} />
                  </View>
                )}
                <View
                  style={[styles.messageRow, msg.user._id === "bot" ? styles.sentRow : styles.receivedRow]}
                >
                  <div
                    className={`message-bubble ${msg.user._id === "bot" || msg.user._id === "Bot" ? 'sent' : 'received'}`}
                  >
                    {msg.mediaType === "audio/ogg" || msg.mediaType?.includes("audio") ?
                      (<ReactAudioPlayer
                        src={msg.media}
                        controls
                        style={{ width: '200' }}
                      />) :
                      msg.mediaType === "image/jpeg" || msg.mediaType === "image/webp" || msg.mediaType === "image/png" || msg.mediaType === "image/jpg" ?
                        (<TouchableOpacity onPress={() => {
                          onDialog(msg.media)
                        }}>
                          <Image
                            source={{ uri: msg.media }}
                            style={{
                              width: 150,
                              aspectRatio: 1, // <--- CLAVE para mantener la proporción
                              height: undefined, // Omitir height o poner undefined
                              marginBottom: 10, color: msg.user._id === "bot" || msg.user._id === "Bot" ? Colors.bgWhite : Colors.blackColor
                            }}
                          />
                        </TouchableOpacity>)
                        : msg.mediaType === "application/pdf" ?
                          (<View style={{ flexWrap: 'wrap', maxWidth: 150 }}>
                            <IconButton
                              style={{ marginEnd: 0 }}
                              icon={'file-pdf-box'}
                              size={50}
                              iconColor={msg.user._id === "bot" || msg.user._id === "Bot" ? Colors.bgWhite : Colors.blackColor}
                              onPress={() => handleOpenPdf(msg.mediaSid)}
                            />
                            <Text style={{ marginBottom: 10, marginTop: -10, color: msg.user._id === "bot" || msg.user._id === "Bot" ? Colors.bgWhite : Colors.blackColor }}>{msg.mediaName}</Text>
                          </View>) : (<div className="message-text" style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                            <WhatsappText
                              text={msg.text}
                              user={msg.user._id}
                            />
                          </div>)
                    }
                    <View style={[styles.messageRow, styles.sentRow]}>
                      <div className="message-info">{new Date(msg.createdAt).toLocaleString()}</div>
                      {msg.user._id === "bot" && ( // Solo mostrar en mensajes enviados por ti
                        <IconButton
                          icon={msg.statusMessage['sent'] === 'all' && msg.statusMessage['delivered'] === 'none' ? 'check' : 'check-all'}
                          size={15}
                          iconColor={msg.statusMessage['read'] === 'all' ? '#34B7F1' : '#999'}
                          style={{ margin: 0, padding: 0 }}
                        />
                      )}
                    </View>
                  </div>
                  {msg.user._id === "bot" && msg?.statusMessage
                    && (msg.statusMessage['undelivered'] === 'all' || msg.statusMessage['failed'] === 'all') && ( // Solo mostrar en mensajes enviados por ti
                      <IconButton
                        icon='alert-circle'
                        size={15}
                        iconColor='red'
                        style={{ margin: 0, padding: 0 }}
                      />
                    )}
                </View>
              </React.Fragment>
            ))}
            <div ref={messagesEndRef} />
          </div>
          {/* BOTÓN FLOTANTE DE MENSAJES NUEVOS */}
          {hasNewMessagesBelow && (
            <TouchableOpacity
              onPress={() => {
                setScrollEnd(true); // Esto activará el useLayoutEffect para bajar
                setHasNewMessagesBelow(false);
              }}
              style={styles.floatingAlertContainer}
            >
              <Surface style={styles.floatingAlertButton} elevation={4}>
                <Text style={styles.floatingAlertLabel}>
                  {/* Icono de flecha abajo o texto */}
                  ⬇ Mensajes nuevos
                </Text>
              </Surface>
            </TouchableOpacity>
          )}
        </View>
        {/* </ScrollView> */}
      </div>
      <View>
        <ChatActionArea
          buttonReset={auth.userInfo.role === Roles.ADMIN || auth.userInfo.id === assignTo}
          onSaveNote={handleNotes}
          notes={chat.notes}
          children={msgClose ? (
            <View >
              <Text style={styles.errorTemplate}>Han pasado más de 24 horas. Selecciona una plantilla para reabrir el chat:</Text>
              <ScrollView style={[styles.container, { maxHeight: 70 }]}>
                {templatesAgrupados[channel.orgId] && templatesAgrupados[channel.orgId]?.map((item) => (
                  <TouchableOpacity
                    key={item.sid}
                    style={styles.buttonP}
                    onPress={() => {
                      handleTemplate(item);
                    }}
                  >
                    <Text style={styles.bodyPreview}>{item.body}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          ) : (<View>
            <form className="message-input-form" onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Escribe un mensaje..."
                className="message-input"
                disabled={!resetIA}
              />
              <Button onPress={openFilePicker} disabled={!resetIA}>
                <Icon
                  source="paperclip"
                  color={!resetIA ? Colors.grayTextButton : Colors.blackColor}
                  size={20}
                />
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }} // Hide the default input element
              />
              <Button
                onPress={handleSend}
                mode={'contained'}
                disabled={!resetIA}
              >
                <Icon
                  source="send"
                  color={!resetIA ? Colors.grayTextButton : Colors.textWhite}
                  size={20}
                />
              </Button>
            </form>
            <View>
              <View style={[styles.horizontalContentTop, {
                marginVertical: 5,
                alignItems: "center",
                flexDirection: "row",
                justifyContent: "space-between"
              }]}>
                <View>
                  {fileToUpload && (
                    <Text style={{ marginVertical: 10 }}>Archivo seleccionado: **{fileToUpload[0].name}**</Text>
                  )}
                </View>
                {auth.userInfo.role === Roles.ADMIN || auth.userInfo.id === assignTo ?
                  (<Button
                    mode={'contained'}
                    onPress={() => handleReset()}
                  >{resetIA ? "Activar IA" : "Pausar IA"}
                  </Button>) : null}
              </View>
            </View>
          </View>
          )}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  floatingAlertContainer: {
    position: 'absolute', // Se posiciona respecto al View padre (el que tiene flex: 1)
    bottom: 15,           // Distancia fija desde el borde inferior del área de CHAT
    left: 0,
    right: 0,
    alignItems: 'center', // Lo centra horizontalmente
    zIndex: 999,          // Crucial: Asegura que esté por encima de todas las burbujas
  },
  floatingAlertButton: {
    backgroundColor: '#6c5ce7', // Violeta
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  floatingAlertLabel: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  errorTemplate: {
    color: Colors.error, margin: 5
  },
  title: { fontSize: 20, fontWeight: 'bold', marginBottom: 15 },
  buttonP: { backgroundColor: Colors.primary, padding: 5, borderRadius: 8, marginBottom: 5 },
  buttonText: { color: 'white', fontWeight: 'bold', fontSize: 10 },
  bodyPreview: { color: 'white', fontSize: 12, opacity: 0.9 },

  messageRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%"
  },
  sentRow: {
    justifyContent: "flex-end",
  },
  receivedRow: {
    justifyContent: "flex-start"
  },
  unreadSeparator: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 20,
    paddingHorizontal: 10,
  },
  lineSeparator: {
    flex: 1,
    height: 1,
    backgroundColor: '#ced4da', // Gris clarito
  },
  unreadText: {
    marginHorizontal: 10,
    color: '#6c757d',
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
  },


  ...GlobalStyles,
});

// Usa una función de comparación personalizada
export const ChatScreen = memo(ChatScreenComponent, (prevProps, nextProps) => {
  // Solo renderiza si el SID del canal cambió
  // Si el padre se actualiza por un mensaje en OTRO chat, esto devolverá true y NO renderizará
  return (prevProps.channel?.sid === nextProps.channel?.sid &&
    prevProps.channel?.user === nextProps.channel?.user &&
    prevProps.channel?.brokerId === nextProps.channel?.brokerId &&
    prevProps.channel?.attributes?.broker_id === nextProps.channel?.attributes?.broker_id)
});